import { Disclosure } from '@headlessui/react'
import classNames from 'classnames'
import { renderContactFieldV2 } from './ContactFieldRendererV2'
import { ChevronToggle } from 'app/components/ChevronToggle'
import { useShowUtmFields, UTM_GATED_FIELD_IDS } from 'app/hooks/useShowUtmFields'
import type { ContactJson } from 'app/types/entities'
import { SectionItem } from 'lib/contactDrawerConfig'

interface ContactAdditionalFieldsV2Props {
  contact: ContactJson
  config?: SectionItem
  isAgentOnly?: boolean
}

export function ContactAdditionalFieldsV2({ contact, config, isAgentOnly = false }: ContactAdditionalFieldsV2Props) {
  const showUtmFields = useShowUtmFields()

  // Use the config structure - this includes ALL available fields from getContactDrawerSections
  // which merges missing fields from default configuration via mergeWithDefaultFields
  const configSubsections = config?.children?.filter((child) => child.visible) || []

  // If no config, don't show anything (should not happen in normal usage)
  if (configSubsections.length === 0) return null

  // Filter sections to only show those with fields that have values
  const visibleSections = configSubsections.filter((subsection) => {
    // Get all field IDs for this subsection from its children
    const fieldIds =
      subsection.children
        ?.filter((child) => child.visible && (showUtmFields || !UTM_GATED_FIELD_IDS.includes(child.id)))
        .map((child) => child.id) || []

    // Check if any field in this subsection would render (has values)
    return fieldIds.some((fieldId) => {
      const fieldElement = renderContactFieldV2({
        fieldId,
        contact,
      })
      return fieldElement !== null
    })
  })

  if (visibleSections.length === 0) return null

  return (
    <div className="flex w-full flex-col items-start gap-4 rounded-md bg-surface p-4">
      <Disclosure>
        {({ open }) => (
          <>
            <Disclosure.Button className="min-h-6 flex w-full items-center gap-x-2">
              <div className="text-heading-xs text-strong">Additional Fields</div>
              <ChevronToggle open={open} />
            </Disclosure.Button>

            <Disclosure.Panel className="w-full">
              {visibleSections.map((subsection, index, array) => {
                // Use the subsection config directly (like V1 does)
                const visibleFields =
                  subsection.children?.filter(
                    (child) => child.visible && (showUtmFields || !UTM_GATED_FIELD_IDS.includes(child.id)),
                  ) || []
                return (
                  <Disclosure
                    as="div"
                    key={`${subsection.id}-${index}`}
                    className={classNames(`w-full rounded-md border border-subtle bg-surface-subtle`, {
                      'mb-1': index < array.length - 1,
                    })}
                  >
                    {({ open: sectionOpen }) => (
                      <div className="flex w-full flex-col">
                        <Disclosure.Button className="flex w-full items-center justify-between py-2 pl-4 pr-2">
                          <div className="flex flex-1 items-center gap-2">
                            <div className="flex items-center gap-2">
                              {subsection.icon && (
                                <subsection.icon
                                  className={`h-4 w-4 text-default ${
                                    subsection.id === `information` ? `rotate-180` : ``
                                  }`}
                                  aria-hidden="true"
                                />
                              )}
                              <div className="text-label-xs text-strong">{subsection.name}</div>
                            </div>
                            <div className="flex-1" />
                            <ChevronToggle open={sectionOpen} className="pr-0" />
                          </div>
                        </Disclosure.Button>

                        <Disclosure.Panel className="px-4 pb-4 pt-0">
                          <div className="grid grid-cols-1">
                            {visibleFields.map((field) => {
                              const fieldElement = renderContactFieldV2({
                                fieldId: field.id,
                                contact,
                              })

                              // Only render if field has a value
                              if (!fieldElement) return null

                              return <div key={field.id}>{fieldElement}</div>
                            })}
                          </div>
                        </Disclosure.Panel>
                      </div>
                    )}
                  </Disclosure>
                )
              })}
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </div>
  )
}
